import { useCallback, useState } from 'react';
import pluralize from 'pluralize';
import toast from 'react-hot-toast';

import { trackEvent } from 'helpers/analytics';
import { useBulkDestroySharedListsMutation } from 'services/listsTree';

/**
 * State + mutation glue for the "Delete" bulk action: confirm-modal
 * open/close and the destroy request with toast + analytics.
 */
export const useBulkDelete = ({ selectedRows, networkId, onDeleted }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [bulkDestroy, { isLoading }] = useBulkDestroySharedListsMutation();

  const count = selectedRows.length;
  const label = pluralize('list', count);

  const open = useCallback(() => setShowConfirm(true), []);
  const close = useCallback(() => setShowConfirm(false), []);

  const confirm = useCallback(async () => {
    const result = await bulkDestroy({ listIds: selectedRows, networkId });

    if (result?.error) {
      toast(`Couldn't delete ${label}, please try again`, { icon: 'danger', id: 'bulk-delete-error' });
      return;
    }

    close();
    toast(`${count} ${label} deleted`, { icon: 'success', id: 'bulk-delete-success' });
    trackEvent('lists:bulk_delete', {
      collectionId: networkId,
      listIds: selectedRows,
      page: 'lists',
      origin: 'bulk_action',
    });
    onDeleted?.({ count });
  }, [bulkDestroy, close, count, label, networkId, onDeleted, selectedRows]);

  return {
    showConfirm,
    isLoading,
    count,
    label,
    title: `Delete ${count} ${label}?`,
    confirmCta: `Delete ${label}`,
    open,
    close,
    confirm,
  };
};
